/*
  SolidForms — Print Reveal intro
  ---------------------------------------------------------------------------
  Runs the emblem print once per session over the homepage, then hands the
  finished part off to the real header logo: the printed emblem flies into the
  header's logo slot while the overlay dissolves, and the actual <img> takes
  over under the cross-fade. The site is never behind a spinner — it is behind
  its own logo being manufactured.

  The intro is unskippable by design: there is no dismiss control and no
  click / key / scroll escape hatch. It ends when the print ends.

  That makes the failsafe load-bearing rather than belt-and-braces. The inline
  gate in snippets/sf-print-reveal-intro.liquid arms a hard timeout that tears
  the overlay down even if this file never loads — without it, one 404 on this
  asset would leave the homepage permanently behind a black screen with no way
  out. Do not remove it.
*/
(function () {
  'use strict';

  var ROOT = document.documentElement;
  var ON = 'sfpr-intro-on';
  var overlay = document.getElementById('sfpr-intro');

  if (!overlay || !ROOT.classList.contains(ON)) return;

  var cfg = {};
  try { cfg = JSON.parse(overlay.getAttribute('data-config') || '{}'); } catch (e) { cfg = {}; }

  // Engine missing (asset 404, blocked script) — get out of the way at once.
  if (typeof window.SFPrintReveal !== 'function') { teardown(true); return; }

  var stage = overlay.querySelector('.sfpr-intro__stage');
  var canvas = overlay.querySelector('.sfpr-intro__canvas');
  var engine = null;

  /* ---------- teardown ---------- */
  // Only two things reach this: the print finishing, and the failsafe. There is
  // deliberately no visitor-triggered path.

  function teardown(immediate) {
    if (teardown.done) return;
    teardown.done = true;
    if (engine) engine.destroy();

    var kill = function () {
      ROOT.classList.remove(ON, 'sfpr-intro-landed');
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      document.dispatchEvent(new CustomEvent('sfpr:intro:end'));
    };

    if (immediate) { kill(); return; }
    overlay.classList.add('is-leaving');
    window.setTimeout(kill, 320);
  }

  /* ---------- the handoff ---------- */

  // A logo that is present but hidden (mobile drawer, middle-center variant)
  // is not a landing pad — pick the first one actually laid out on screen.
  function findLogo() {
    var sel = cfg.logoSelector || '.header__heading-logo';
    var nodes = document.querySelectorAll(sel);
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var r = n.getBoundingClientRect();
      if (n.offsetParent !== null && r.width > 8 && r.height > 8 && r.top < window.innerHeight) return n;
    }
    return null;
  }

  // Flight choreography. The swap happens 40ms AFTER the transform settles, so
  // the printed part is motionless at the target when the real logo replaces it.
  var FLIGHT = 760, SWAP = 800, GONE = 880;

  function handoff() {
    var logo = findLogo();
    var src = engine.partRect();

    if (!logo || !src.width) { teardown(false); return; }

    var dst = logo.getBoundingClientRect();
    var scale = dst.width / src.width;
    var dx = dst.left + dst.width / 2 - src.cx;
    var dy = dst.top + dst.height / 2 - src.cy;

    stage.style.transformOrigin = src.originX + 'px ' + src.originY + 'px';
    overlay.classList.add('is-handing-off');

    // The part is white-hot plastic; the logo asset is flat black. Morph the
    // beads late in the flight, once the emblem is clearly on its way up, and
    // drop the glow and drop-shadow that would otherwise leave a soft halo the
    // real logo does not have. The engine has stopped rendering by now, so
    // these inline styles are not overwritten on a later frame.
    // The base layer sits at translateZ(2px) and the top at translateZ(11px);
    // even with the camera flat, perspective offsets them from each other, so
    // the base reads as a soft smear under every bead. Harmless during the
    // print, but it makes the landed mark blurrier than the logo asset — and
    // once the part is flat black the base contributes nothing. Fade it out.
    var ink = cfg.inkColor || '#000';
    var tops = canvas.querySelectorAll('.sfpr__bar-top');
    var shadows = canvas.querySelectorAll('.sfpr__bar-shadow');
    var bases = canvas.querySelectorAll('.sfpr__bar-base');
    var wash = canvas.querySelector('.sfpr__wash');

    window.requestAnimationFrame(function () {
      // Second frame, so the origin and the starting styles are committed
      // before the transitions are allowed to run.
      window.requestAnimationFrame(function () {
        stage.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(' + scale + ')';

        for (var i = 0; i < tops.length; i++) {
          tops[i].style.transition = 'background-color 320ms ease 300ms, box-shadow 280ms ease 200ms';
          tops[i].style.background = ink;
          tops[i].style.boxShadow = 'none';
        }
        for (var j = 0; j < shadows.length; j++) {
          shadows[j].style.transition = 'opacity 280ms ease 200ms';
          shadows[j].style.opacity = '0';
        }
        for (var k = 0; k < bases.length; k++) {
          bases[k].style.transition = 'opacity 280ms ease 200ms';
          bases[k].style.opacity = '0';
        }
        if (wash) {
          wash.style.transition = 'opacity 240ms ease';
          wash.style.opacity = '0';
        }
      });
    });

    // Atomic swap in a single frame: the real logo appears and the printed part
    // disappears together, with no cross-fade. Two congruent black shapes
    // cross-fading would dip in opacity and read as a flicker; swapping them in
    // one paint reads as nothing at all.
    window.setTimeout(function () {
      overlay.classList.add('is-landed');
      ROOT.classList.add('sfpr-intro-landed');
    }, SWAP);
    window.setTimeout(function () { teardown(true); }, GONE);
  }

  /* ---------- run ---------- */

  engine = new window.SFPrintReveal(canvas, {
    hot: cfg.hot || '#ff7a1a',
    grid: cfg.grid !== false,
    hud: cfg.hud !== false,
    speed: Number(cfg.speed) > 0 ? Number(cfg.speed) : 1.8,
    mode: 'manual',
    padding: Number(cfg.padding) > 0 ? Number(cfg.padding) : 1.5,
    onEnd: handoff
  });

  // Swallow taps and drags that land on the overlay so they neither dismiss the
  // intro nor fall through to the page underneath. touchmove has to be
  // non-passive to be cancellable — this is the one place that is worth it.
  var swallow = function (e) { e.preventDefault(); e.stopPropagation(); };
  overlay.addEventListener('click', swallow);
  overlay.addEventListener('touchmove', swallow, { passive: false });

  engine.play(true);
})();
